import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { useGet } from "@/lib/api";
import { useSeo } from "@/lib/store";
import { ProductGrid } from "@/components/ProductCard";

const TOP = ["S", "M", "L", "XL", "XXL"], BOT = ["28", "30", "32", "34", "36"];
const TAGS = [["", "All"], ["trending", "Trending"], ["new", "New"], ["bestseller", "Best sellers"], ["limited", "Limited"]];

export default function Shop() {
  const { category = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const { data: cats = [] } = useGet("/categories");
  const q = sp.get("q") || "", sort = sp.get("sort") || "new", tag = sp.get("tag") || "", size = sp.get("size") || "", max = sp.get("max") || "";
  const qs = new URLSearchParams({ category, q, sort, tag, size, ...(max ? { max_price: max } : {}) }).toString();
  const { data = [], isLoading } = useGet(`/products?${qs}`);
  const cat = cats.find((c) => c.slug === category);
  useSeo(cat?.name || "Shop All Streetwear", `Shop ${cat?.name || "premium streetwear"} at DEN & THREADS — oversized tees, hoodies, anime prints, baggy jeans.`);
  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n); };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-8 py-12" data-testid="shop-page">
      <p className="eyebrow">{data.length} pieces</p>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-3 leading-none">{q ? <>RESULTS: “{q}”</> : cat?.name || "SHOP THE DROP"}</h1>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mt-8 pb-2" data-testid="shop-category-chips">
        <Link to={`/shop?${sp}`} className={`chip shrink-0 ${!category ? "on" : ""}`}>All</Link>
        {cats.map((c) => <Link key={c.slug} to={`/shop/${c.slug}?${sp}`} data-testid={`shop-cat-${c.slug}`} className={`chip shrink-0 ${category === c.slug ? "on" : ""}`}>{c.name}</Link>)}
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-6 py-4 border-y border-white/10">
        <SlidersHorizontal className="w-4 h-4 text-neon" />
        {TAGS.map(([v, l]) => <button key={l} onClick={() => set("tag", v)} data-testid={`filter-tag-${v || "all"}`} className={`chip ${tag === v ? "on" : ""}`}>{l}</button>)}
        <select value={size} onChange={(e) => set("size", e.target.value)} className="field w-auto py-2" data-testid="filter-size-select">
          <option value="">Any size</option>{[...TOP, ...BOT].map((s) => <option key={s}>{s}</option>)}
        </select>
        <select value={max} onChange={(e) => set("max", e.target.value)} className="field w-auto py-2" data-testid="filter-price-select">
          <option value="">Any price</option><option value="999">Under ₹999</option><option value="1499">Under ₹1,499</option><option value="1999">Under ₹1,999</option><option value="2499">Under ₹2,499</option>
        </select>
        <select value={sort} onChange={(e) => set("sort", e.target.value)} className="field w-auto py-2 ml-auto" data-testid="sort-select">
          <option value="new">Newest</option><option value="popular">Most popular</option><option value="price_asc">Price: Low → High</option><option value="price_desc">Price: High → Low</option>
        </select>
        {q && <button onClick={() => set("q", "")} className="chip flex items-center gap-1" data-testid="clear-search">{q} <X className="w-3 h-3" /></button>}
      </div>
      <div className="mt-10">
        {isLoading ? <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">{[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#171717] animate-pulse" />)}</div>
          : data.length ? <ProductGrid items={data} /> : <div className="py-24 text-center" data-testid="shop-empty"><p className="font-display text-4xl">NOTHING HERE. YET.</p><Link to="/shop" className="btn-ghost mt-6">Reset filters</Link></div>}
      </div>
    </div>
  );
}
